import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import {
    updateSale,
    getClientOptions,
} from '../../../actions/sale';
import { getFirmOptionsForManagers } from '../../../actions/user';
import { getSparepartOptions, getWarehouseOptions } from '../../../actions/spare_part';
import { getSelectedOption } from '../../../utils/hooks/getParams';

const SparepartSaleUpdateForm = ({ onClose }) => {
    const dispatch = useDispatch();
    const { sale, user, spare_part } = useSelector(state => state);
    const {
        client_options,
        error,
        loading,
        update_sale: initialSale,
        show
    } = sale;
    const { firm_options } = user;
    const { warehouse_options, spare_part_options } = spare_part;

    const defaultValues = {
        is_special_sale: false,
        firm_id: '',
        client_id: '',
        warehouse_id: '',
        date: moment().format('YYYY-MM-DD'),
        transport_number: '',
        driver_name: '',
        description: '',
        items: []
    };

    const [inputs, setInputs] = useState(defaultValues);
    const [errors, setErrors] = useState({});
    const [firmOptions, setFirmOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [sparepartOptions, setSparepartOptions] = useState([]);
    const [selectedSparepartOptions, setSelectedSparepartOptions] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);
    const [newSparepart, setNewSparepart] = useState(null);

    useEffect(() => {
        if (initialSale) {
            setInputs({
                firm_id: initialSale.firm_id,
                client_id: initialSale.client_id,
                warehouse_id: initialSale.warehouse_id,
                date: moment(initialSale.date, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                transport_number: initialSale.transport_number,
                driver_name: initialSale.driver_name,
                description: initialSale.description,
                items: initialSale.items || []
            });

            if (initialSale.items) {
                // Filter out deleted items first
                const activeItems = initialSale.items.filter(item => !item.deleted_at);
                
                const selectedSpareparts = activeItems.map(item => ({
                    id: item.sellable_id,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    net_price: item.net_price,
                    in_stock: item.sellable?.in_stock || item.in_stock,
                    unit: item.unit,
                    original_quantity: item.quantity // Store the original quantity sold
                }));
                
                // Remove any potential duplicates by spare part ID
                const uniqueSpareparts = selectedSpareparts.filter((sparepart, index, self) => 
                    index === self.findIndex(s => s.id === sparepart.id)
                );
                
                setSelectedSparepartOptions(uniqueSpareparts);
            }

            // Load spare parts for the initial firm and warehouse
            if (initialSale.firm_id && initialSale.warehouse_id) {
                dispatch(getSparepartOptions({ 
                    is_for_sale: true,
                    warehouse_id: initialSale.warehouse_id,
                    firm_id: initialSale.firm_id
                }));
            }
        }
    }, [initialSale]);

    useEffect(() => {
        dispatch(getClientOptions());
        dispatch(getFirmOptionsForManagers());
        dispatch(getWarehouseOptions({ type: 'part' }));
    }, []);

    useEffect(() => {
        if (inputs.firm_id) {
            dispatch(getWarehouseOptions({ firm_id: inputs.firm_id, type: 'part' }));
        }
    }, [inputs.firm_id]);

    useEffect(() => {
        if (inputs.warehouse_id && inputs.firm_id) {
            dispatch(getSparepartOptions({ 
                is_for_sale: true,
                warehouse_id: inputs.warehouse_id,
                firm_id: inputs.firm_id
            }));
        }
    }, [inputs.warehouse_id, inputs.firm_id]);

    useEffect(() => {
        let items = [];
        firm_options?.forEach((item) => items.push({
            label: item.name,
            value: item.id
        }));
        setFirmOptions(items);
    }, [firm_options]);

    useEffect(() => {
        let options = [];
        warehouse_options?.forEach((item) => options.push({
            label: item.name,
            value: item.id
        }));
        setWarehouseOptions(options);
    }, [warehouse_options]);

    useEffect(() => {
        let options = [];
        client_options?.forEach((item) => options.push({
            label: item.name,
            value: item.id
        }));
        setClientOptions(options);
    }, [client_options]);

    useEffect(() => {
        if (spare_part_options?.length) {
            const options = spare_part_options.map(sparepart => ({
                id: sparepart.id,
                name: sparepart.name,
                net_price: sparepart.price,
                in_stock: sparepart.in_stock,
                unit: sparepart.unit
            }));
            setSparepartOptions(options);

            // Update in_stock values for existing selected spare parts
            setSelectedSparepartOptions(prev => prev.map(selected => {
                const updatedSparepart = spare_part_options.find(sp => sp.id === selected.id);
                if (updatedSparepart) {
                    return {
                        ...selected,
                        in_stock: updatedSparepart.in_stock
                    };
                }
                return selected;
            }));
        }
    }, [spare_part_options]);

    const handleError = (errors) => {
        setErrors(errors);
    };

    const clearErrors = () => {
        setErrors({});
    };

    const handleAddSparepart = () => {
        if (newSparepart) {
            const sparepartToAdd = sparepartOptions.find(sp => sp.id === newSparepart.value);
            if (sparepartToAdd) {
                // Check if spare part has stock
                if (sparepartToAdd.in_stock <= 0) {
                    setErrors({
                        spare_part_no_stock: ["Bu ehtiyot qismning omborda qoldig'i yo'q"]
                    });
                    setTimeout(() => {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.spare_part_no_stock;
                            return newErrors;
                        });
                    }, 3000);
                    setNewSparepart(null);
                    return;
                }

                // Check if this spare part is already selected
                const exists = selectedSparepartOptions.some(sp => sp.id === sparepartToAdd.id);
                if (!exists) {
                    const newSparepartOption = {
                        id: sparepartToAdd.id,
                        name: sparepartToAdd.name,
                        quantity: 0,
                        price: sparepartToAdd.net_price,
                        net_price: sparepartToAdd.net_price,
                        in_stock: sparepartToAdd.in_stock,
                        unit: sparepartToAdd.unit
                    };
                    setSelectedSparepartOptions(prev => [...prev, newSparepartOption]);
                } else {
                    // Show error message if trying to add duplicate
                    setErrors({
                        spare_part_duplicate: ["Bu ehtiyot qism allaqachon tanlangan"]
                    });
                    setTimeout(() => {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.spare_part_duplicate;
                            return newErrors;
                        });
                    }, 3000);
                }
            }
            setNewSparepart(null);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = {};

        if (!inputs.firm_id) {
            validationErrors.firm_id = ["Firma tanlash majburiy"];
        }

        if (!inputs.client_id) {
            validationErrors.client_id = ["Mijoz tanlash majburiy"];
        }

        if (!inputs.warehouse_id) {
            validationErrors.warehouse_id = ["Ombor tanlash majburiy"];
        }

        if (!inputs.transport_number) {
            validationErrors.transport_number = ["Transport raqami majburiy"];
        }

        if (!inputs.driver_name) {
            validationErrors.driver_name = ["Haydovchi ismi majburiy"];
        }

        if (!selectedSparepartOptions.length) {
            validationErrors.items = ["Ehtiyot qismlar tanlash majburiy"];
        } else {
            // Check spare part quantities
            const invalidQuantities = selectedSparepartOptions.filter(item => {
                const maxAllowed = item.in_stock + (item.original_quantity || 0);
                return !item.quantity || item.quantity <= 0 || item.quantity > maxAllowed;
            });

            if (invalidQuantities.length > 0) {
                validationErrors['items.0.quantity'] = ["Miqdor ombordagi qoldiqdan ko'p bo'lmasligi kerak"];
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            handleError(validationErrors);
            return;
        }

        const data = {
            client_id: parseInt(inputs.client_id),
            firm_id: parseInt(inputs.firm_id),
            warehouse_id: parseInt(inputs.warehouse_id),
            date: inputs.date,
            transport_number: inputs.transport_number,
            driver_name: inputs.driver_name,
            description: inputs.description,
            comment: inputs.description,
            items: selectedSparepartOptions.map(item => ({
                sellable_type: 'App\\Models\\SparePart',
                sellable_id: parseInt(item.id),
                quantity: parseFloat(item.quantity),
                price: parseFloat(item.price)
            }))
        };

        try {
            const response = await dispatch(updateSale(initialSale.id, data, 1));
            if (response !== true) {
                return;
            }
            onClose();
        } catch (err) {
            if (err.errors) {
                handleError(err.errors);
            }
        }
    };

    const handleSelect = (data, input) => {
        clearErrors();
        setInputs(state => ({
            ...state,
            [input.name]: data?.value || ''
        }));
    };

    const handleChange = (event) => {
        clearErrors();
        setInputs(state => ({
            ...state,
            [event.target.name]: event.target.value
        }));
    };

    const onChangeInput = (e) => {
        const { name, value } = e.target;
        const itemId = parseInt(e.target.dataset.id);
        
        const updatedItems = selectedSparepartOptions.map(item => {
            if (item.id === itemId) {
                let newValue = parseFloat(value) || 0;
                
                // Prevent entering quantities higher than available stock + original quantity
                const maxAllowed = item.in_stock + (item.original_quantity || 0);
                if (name === 'quantity' && newValue > maxAllowed) {
                    newValue = maxAllowed;
                    // Show a warning message
                    setErrors(prev => ({
                        ...prev,
                        [`quantity_${itemId}`]: [`Maksimal miqdor: ${maxAllowed}`]
                    }));
                    
                    // Clear the error after 3 seconds
                    setTimeout(() => {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`quantity_${itemId}`];
                            return newErrors;
                        });
                    }, 3000);
                } else if (name === 'quantity') {
                    // Clear any existing quantity error for this item
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`quantity_${itemId}`];
                        return newErrors;
                    });
                }
                
                return { ...item, [name]: newValue };
            }
            return item;
        });
        
        setSelectedSparepartOptions(updatedItems);
    };

    const onRemoveSelectedSparepart = (id) => {
        const updatedSelectedSpareparts = selectedSparepartOptions.filter(item => item.id !== id);
        setSelectedSparepartOptions(updatedSelectedSpareparts);
    };

    const availableSparepartOptions = sparepartOptions
        .filter(sp => !selectedSparepartOptions.some(selected => selected.id === sp.id))
        .filter(sp => sp.in_stock > 0) // Only show spare parts with stock
        .map(sp => ({
            label: `${sp.name} (${sp.in_stock} ${sp.unit?.symbol || 'dona'})`,
            value: sp.id
        }));

    return (
        <form onSubmit={handleSubmit}>
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Firma</label>
                        <Select
                            value={getSelectedOption(inputs.firm_id, firmOptions)}
                            name="firm_id"
                            onChange={(data) => handleSelect(data, {name: 'firm_id'})}
                            options={firmOptions}
                            placeholder="Firmani tanlang"
                            className={`react-select-container ${errors.firm_id ? 'is-invalid' : ''}`}
                        />
                        {errors.firm_id && <div className="invalid-feedback">{errors.firm_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Ombor</label>
                        <Select
                            value={getSelectedOption(inputs.warehouse_id, warehouseOptions)}
                            name="warehouse_id"
                            onChange={(data) => handleSelect(data, {name: 'warehouse_id'})}
                            options={warehouseOptions}
                            placeholder="Omborni tanlang"
                            className={`react-select-container ${errors.warehouse_id ? 'is-invalid' : ''}`}
                        />
                        {errors.warehouse_id && <div className="invalid-feedback">{errors.warehouse_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Mijoz</label>
                        <Select
                            value={getSelectedOption(inputs.client_id, clientOptions)}
                            name="client_id"
                            onChange={(data) => handleSelect(data, {name: 'client_id'})}
                            options={clientOptions}
                            placeholder="Mijozni tanlang"
                            className={`react-select-container ${errors.client_id ? 'is-invalid' : ''}`}
                        />
                        {errors.client_id && <div className="invalid-feedback">{errors.client_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="date">Sana</label>
                        <input
                            type="date"
                            name="date"
                            className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                            max={moment().format('YYYY-MM-DD')}
                            value={inputs?.date || ''}
                            onChange={handleChange}
                        />
                        {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="transport_number">Transport raqami</label>
                        <input
                            type="text"
                            name="transport_number"
                            className={`form-control ${errors.transport_number ? 'is-invalid' : ''}`}
                            value={inputs?.transport_number || ''}
                            onChange={handleChange}
                            placeholder="Transport raqamini kiriting"
                        />
                        {errors.transport_number && <div className="invalid-feedback">{errors.transport_number}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="driver_name">Haydovchi ismi</label>
                        <input
                            type="text"
                            name="driver_name"
                            className={`form-control ${errors.driver_name ? 'is-invalid' : ''}`}
                            value={inputs?.driver_name || ''}
                            onChange={handleChange}
                            placeholder="Haydovchi ismini kiriting"
                        />
                        {errors.driver_name && <div className="invalid-feedback">{errors.driver_name}</div>}
                    </div>
                </div>
                <div className="col-md-12">
                    <div className="form-group">
                        <label htmlFor="description">Izoh</label>
                        <textarea
                            name="description"
                            className="form-control"
                            rows="3"
                            value={inputs?.description || ''}
                            onChange={handleChange}
                            placeholder="Izoh kiriting"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-4">
                <h5>Ehtiyot qismlar</h5>
                <div className="row g-3 mb-3">
                    <div className="col-md-8">
                        <Select
                            value={newSparepart}
                            onChange={setNewSparepart}
                            options={availableSparepartOptions}
                            placeholder="Ehtiyot qism tanlang"
                            isClearable
                        />
                    </div>
                    <div className="col-md-4">
                        <Button 
                            type="button" 
                            variant="primary" 
                            onClick={handleAddSparepart}
                            disabled={!newSparepart}
                        >
                            Qo'shish
                        </Button>
                    </div>
                </div>
                
                {errors.spare_part_duplicate && (
                    <div className="alert alert-danger" role="alert">
                        {errors.spare_part_duplicate[0]}
                    </div>
                )}
                
                {errors.spare_part_no_stock && (
                    <div className="alert alert-danger" role="alert">
                        {errors.spare_part_no_stock[0]}
                    </div>
                )}

                {selectedSparepartOptions.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Ehtiyot qism</th>
                                    <th>Ombordagi miqdor</th>
                                    <th>Narx</th>
                                    <th>Miqdor</th>
                                    <th>Sotuv narxi</th>
                                    <th>Jami</th>
                                    <th>Amallar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedSparepartOptions.map((sparepart) => (
                                    <tr key={sparepart.id}>
                                        <td>{sparepart.name}</td>
                                        <td>{sparepart.in_stock} {sparepart.unit?.symbol || 'dona'}</td>
                                        <td>{sparepart.net_price}</td>
                                        <td>
                                            <input
                                                type="number"
                                                className={`form-control form-control-sm ${
                                                    errors[`quantity_${sparepart.id}`] ? 'is-invalid' : ''
                                                } ${
                                                    sparepart.quantity > (sparepart.in_stock + (sparepart.original_quantity || 0)) ? 'border-danger' : ''
                                                }`}
                                                name="quantity"
                                                data-id={sparepart.id}
                                                value={sparepart?.quantity || ''}
                                                onChange={onChangeInput}
                                                min="0"
                                                max={sparepart.in_stock + (sparepart.original_quantity || 0)}
                                                step="0.01"
                                            />
                                            {errors[`quantity_${sparepart.id}`] && (
                                                <div className="invalid-feedback d-block">
                                                    {errors[`quantity_${sparepart.id}`][0]}
                                                </div>
                                            )}
                                            {sparepart.quantity > (sparepart.in_stock + (sparepart.original_quantity || 0)) && (
                                                <small className="text-danger">
                                                    Maksimal miqdor: {sparepart.in_stock + (sparepart.original_quantity || 0)} {sparepart.unit?.symbol || 'dona'}
                                                </small>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                name="price"
                                                data-id={sparepart.id}
                                                value={sparepart?.price || ''}
                                                onChange={onChangeInput}
                                                min="0"
                                                step="0.01"
                                            />
                                        </td>
                                        <td>
                                            {((sparepart?.quantity || 0) * (sparepart?.price || 0)).toFixed(2)}
                                        </td>
                                        <td>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => onRemoveSelectedSparepart(sparepart.id)}
                                            >
                                                O'chirish
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {errors.items && <div className="text-danger">{errors.items}</div>}
            </div>

            <div className="mt-4 d-flex justify-content-end">
                <Button variant="secondary" onClick={onClose} className="me-2">
                    Bekor qilish
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Saqlanmoqda...' : 'Saqlash'}
                </Button>
            </div>
        </form>
    );
};

export default SparepartSaleUpdateForm; 